import { useEffect, useState } from 'react'
import { Users, RefreshCw, DollarSign, TrendingUp, Link as LinkIcon, Gift } from 'lucide-react'

interface AffiliatlyData {
  totalAffiliates: number
  activeAffiliates: number
  totalEarnings: number
  totalOrders: number
  recentAffiliates: Array<{
    id: string
    name: string
    email: string
    earnings: number
    orders: number
    status: string
    joinDate: string
  }>
  topPerformers: Array<{
    id: string
    name: string
    earnings: number
    orders: number
  }>
}

export default function AffiliatlyPage() {
  const [data, setData] = useState<AffiliatlyData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAffiliatlyData()
  }, [])

  const fetchAffiliatlyData = async () => {
    setLoading(true)
    // TODO: Implementar chamada real à API do Affiliatly
    setTimeout(() => {
      setData({
        totalAffiliates: 0,
        activeAffiliates: 0,
        totalEarnings: 0,
        totalOrders: 0,
        recentAffiliates: [],
        topPerformers: []
      })
      setLoading(false)
    }, 1000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Affiliatly</h1>
          <p className="text-gray-600 mt-1">Gestão de programa de afiliados</p>
        </div>
        <button
          onClick={fetchAffiliatlyData}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Afiliados</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{data?.totalAffiliates || 0}</p>
            </div>
            <Users className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Afiliados Ativos</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{data?.activeAffiliates || 0}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ganhos Totais</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">€{data?.totalEarnings.toFixed(2) || '0.00'}</p>
            </div>
            <DollarSign className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Encomendas</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{data?.totalOrders || 0}</p>
            </div>
            <LinkIcon className="w-8 h-8 text-black" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Afiliados Recentes</h2>
          </div>
          <div className="p-6">
            {data?.recentAffiliates.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhum afiliado encontrado</p>
            ) : (
              <div className="space-y-4">
                {data?.recentAffiliates.map((affiliate) => (
                  <div key={affiliate.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{affiliate.name}</p>
                      <p className="text-sm text-gray-500">{affiliate.email}</p>
                      <p className="text-xs text-gray-400 mt-1">Registado em {affiliate.joinDate}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">€{affiliate.earnings.toFixed(2)}</p>
                      <p className="text-sm text-gray-500">{affiliate.orders} encomendas</p>
                      <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${
                        affiliate.status === 'Ativo' 
                          ? 'bg-gray-100 text-black' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {affiliate.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Top Performers</h2>
          </div>
          <div className="p-6">
            {data?.topPerformers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhum dado disponível</p>
            ) : (
              <div className="space-y-4">
                {data?.topPerformers.map((affiliate, index) => (
                  <div key={affiliate.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{affiliate.name}</p>
                        <p className="text-sm text-gray-500">{affiliate.orders} encomendas</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">€{affiliate.earnings.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Ações Rápidas</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-left">
              <LinkIcon className="w-6 h-6 text-black" />
              <div>
                <p className="font-medium text-gray-900">Gerar Link de Afiliado</p>
                <p className="text-sm text-gray-500">Criar novo link de rastreamento</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-left">
              <Gift className="w-6 h-6 text-black" />
              <div>
                <p className="font-medium text-gray-900">Gift Cards</p>
                <p className="text-sm text-gray-500">Pagar com gift cards</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-left">
              <DollarSign className="w-6 h-6 text-black" />
              <div>
                <p className="font-medium text-gray-900">Pagamentos em Massa</p>
                <p className="text-sm text-gray-500">Pagar múltiplos afiliados</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
